import type { RefObject } from 'react';

type TeleopControlPadProps = {
  zAreaRef: RefObject<HTMLDivElement | null>;
  joystickRef: RefObject<HTMLDivElement | null>;
  controlsEnabled: boolean;
  showDesktopRotationKeyboardHint: boolean;
  shakeAnimation: string;
  zAngle: number;
  knobOffset: {
    x: number;
    y: number;
  };
};

const TeleopControlPad: React.FC<TeleopControlPadProps> = ({
  zAreaRef,
  joystickRef,
  controlsEnabled,
  showDesktopRotationKeyboardHint,
  shakeAnimation,
  zAngle,
  knobOffset,
}) => {
  const disabledPointerClass = controlsEnabled
    ? ''
    : 'pointer-events-none';

  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-end justify-between px-7 pb-[calc(env(safe-area-inset-bottom,0px)+28px)] max-[900px]:px-4 max-[900px]:pb-[calc(env(safe-area-inset-bottom,0px)+20px)]">
      <div
        className="pointer-events-auto flex w-full items-end justify-between"
        style={{ animation: shakeAnimation }}
      >
        <div
          ref={zAreaRef}
          className={`relative flex h-[180px] w-[180px] items-center justify-center rounded-full border border-white/10 bg-white/5 shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl touch-none max-[900px]:h-[160px] max-[900px]:w-[160px] max-[600px]:h-[126px] max-[600px]:w-[126px] ${disabledPointerClass}`}
        >
          <div className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/70">
            Rotate
          </div>
          {showDesktopRotationKeyboardHint ? (
            <>
              <div className="pointer-events-none absolute left-[14%] top-1/2 -translate-y-1/2 rounded-full border border-white/12 bg-black/30 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/65 shadow-[0_8px_20px_rgba(0,0,0,0.25)] backdrop-blur-md">
                A
              </div>
              <div className="pointer-events-none absolute right-[14%] top-1/2 -translate-y-1/2 rounded-full border border-white/12 bg-black/30 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/65 shadow-[0_8px_20px_rgba(0,0,0,0.25)] backdrop-blur-md">
                D
              </div>
            </>
          ) : null}
          <div className="absolute inset-[22%] rounded-full border border-white/10 bg-black/30" />
          <div
            className="pointer-events-none absolute left-1/2 top-1/2 h-[65%] w-[2px] -translate-x-1/2 -translate-y-1/2"
            style={{ transform: `translate(-50%, -50%) rotate(${zAngle}deg)` }}
          >
            <div className="absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_12px_rgba(69,240,223,0.8)]" />
            <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 rounded-full bg-primary/70" />
          </div>
        </div>

        <div
          ref={joystickRef}
          className={`relative flex h-[180px] w-[180px] items-center justify-center rounded-full border border-white/10 bg-white/5 shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl touch-none max-[900px]:h-[160px] max-[900px]:w-[160px] max-[600px]:h-[126px] max-[600px]:w-[126px] ${disabledPointerClass}`}
        >
          <div className="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/70">
            Move
          </div>
          <div
            className="h-[70px] w-[70px] rounded-full bg-[#f7f8fc] shadow-[0_0_25px_rgba(69,240,223,0.6)] transition-transform duration-75 max-[900px]:h-[62px] max-[900px]:w-[62px] max-[600px]:h-[49px] max-[600px]:w-[49px]"
            style={{
              transform: `translate(${knobOffset.x}px, ${knobOffset.y}px)`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TeleopControlPad;
